import { FocusStyleManager, Position, Toast, Toaster } from '@blueprintjs/core';
import React, { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import AppOverlay from '../../features/appRun/components/AppOverlay';
import { notificationActions } from '../../state/notification/notificationActions';
import '../../styles/main.scss';
import '../../styles/fonts.scss';
import { IRootState } from '../../state/rootReducer';
import { useSelector, useDispatch } from 'react-redux';
import Footer from '../atoms/Footer';
import * as queryString from 'query-string';
import { navigate } from 'gatsby';
import WebclipLogoIcon from '../../assets/logos/biolib-webclip-icon.png';
import clsx from 'clsx';
import Navbar from '@components/organisms/Navbar';
import { useAuthStatusRedirect } from '../../apiHooks/useAuthRedirect';
import { base64ToUtf8 } from '../../apiHooks/utils';

// Do not show blue box around all Blueprint buttons
FocusStyleManager.onlyShowFocusOnTabs();

interface IProps {
    location: Location;
    children: React.ReactNode;
}

function PageWrapperLayout(props: IProps) {
    const { location, children } = props;
    useAuthStatusRedirect();

    const toaster = useRef<Toaster | null>(null);
    const toasts = useSelector((state: IRootState) => state.notification.toasts);

    const dispatch = useDispatch();
    const addErrorToast = useCallback((message: string) => dispatch(notificationActions.addError(message)), [dispatch]);
    const clearToasts = useCallback(() => dispatch(notificationActions.clearToasts()), [dispatch]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const queryParams = queryString.parse(location.search.replace('?', ''));
            if (typeof queryParams?.error == 'string') {
                try {
                    const errorDict: { [errorKey: string]: { message: string } } = JSON.parse(
                        base64ToUtf8(decodeURIComponent(queryParams?.error)),
                    );

                    // TODO: Write redux saga to handle the error dict instead of just creating a toast
                    for (const { message } of Object.values(errorDict)) {
                        addErrorToast(message);
                    }
                    navigate(location.pathname);
                } catch (error) {
                    console.error('Failed to parse error in URL parameters: ', error);
                }
            }
        }
    }, [location.search, location.pathname, addErrorToast]);

    useEffect(() => {
        if (toasts.length > 0) {
            toasts.forEach((toastProps) => {
                if (toaster.current !== undefined) {
                    toaster.current.show(toastProps);
                }
            });
            clearToasts();
        }
    }, [toasts]);

    const path = location.pathname;
    const isEmbedded = path.startsWith('/embed/') || path.startsWith('/client-routes/embed/');
    const isPopup = path === '/sign-in/' && new URLSearchParams(location.search).get('popup') === 'true';
    const hideNavbarAndFooter = isEmbedded || isPopup;
    const isDocs = path.startsWith('/docs/');

    return (
        <div onDragOver={(event) => event.preventDefault()} onDrop={(event) => event.preventDefault()}>
            <Helmet>
                <meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0' />
                <link href={WebclipLogoIcon as string} rel='apple-touch-icon' sizes='256x256' />
                <html lang='en' />
            </Helmet>

            <Toaster position={Position.TOP} ref={(ref: Toaster) => (toaster.current = ref)}>
                {toasts.map((toast, index) => (
                    <Toast key={index} {...toast} />
                ))}
            </Toaster>

            <AppOverlay />

            {!hideNavbarAndFooter && <Navbar location={location} />}
            <div className='flex flex-col h-screen w-screen'>
                <div className={clsx('flex-1', isDocs ? 'bg-white' : 'bg-default')}>
                    {!hideNavbarAndFooter && <div className='print:hidden h-14' />}
                    {children}
                </div>
                {hideNavbarAndFooter ? null : (
                    <>
                        <div className={clsx('flex-auto', isDocs ? 'bg-white' : 'bg-default')} />
                        <div className='flex-initial bg-default print:hidden' id='footer'>
                            <Footer />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default function PageWrapper({ element, props }: any) {
    // Gatsby requires this wrapper to pass props to the layout component.
    return <PageWrapperLayout {...props}>{element}</PageWrapperLayout>;
}
